<template>
  <v-dialog
    :value="value"
    width="70%"
    scrollable
    @input="$emit('input', $event)"
  >
    <v-card v-if="cell" class="rounded-lg">
      <v-card-title class="title primary--text text--darken-3 px-4 py-2">
        {{ cell.outcomeName }} by {{ cell.predictorName }}
        <v-spacer></v-spacer>
        <v-btn icon @click="$emit('input', false)">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pt-3">
        <div class="dd-caption mb-2">
          <span v-for="l in cell.levels" :key="'cd-l-' + l" class="mr-3">
            <b>{{ l }}</b> n={{ groupSize(l) }}
          </span>
        </div>

        <v-sheet outlined rounded class="pa-3 mb-3">
          <div class="d-flex align-center flex-wrap">
            <div class="mr-4">
              <div class="dd-test-name">{{ testName }}</div>
              <div class="dd-caption">{{ comparisonText }}</div>
            </div>

            <v-spacer />

            <div class="text-right">
              <div v-if="loading" class="dd-caption">computing&hellip;</div>
              <template v-else>
                <div :class="['dd-p-value', { 'dd-sig': significant }]">
                  {{ pValueLabel }}
                </div>
                <div class="dd-caption">{{ significanceLabel }}</div>
              </template>
            </div>
          </div>
        </v-sheet>

        <plotly-chart
          v-if="traces.length > 0"
          :key="cell.key"
          :data="traces"
          :layout="layout"
          :display-mode-bar="true"
        />
        <div
          v-else
          class="title primary--text text--lighten-5 py-5"
          align="center"
        >
          NO DATA AT BASELINE
        </div>

        <group-stats-table
          v-if="cell.continuous && stats"
          :rows="stats.statRows"
          :reference-level="cell.referenceLevel"
          :p-values="stats.pairwise"
          :unavailable-label="stats.pairwiseLabel"
        />

        <contingency-table
          v-else-if="!cell.continuous"
          :table="cell.tableView"
        />

        <div v-if="cell.disclosure" class="dd-caption mt-2">
          <v-icon x-small color="#777" class="mr-1">mdi-information</v-icon>
          {{ cell.disclosure }}
        </div>

        <div v-if="stats && stats.error" class="dd-caption mt-1">
          <v-icon x-small color="#c62828" class="mr-1">
            mdi-alert-circle-outline
          </v-icon>
          {{ stats.error }}
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  MEAN_MARKER_SIZE_LARGE,
  boxTraces,
  formatPValue,
  stackedBarTraces,
} from '@/utils/dashboard';
import PlotlyChart from '@/components/common/PlotlyChart.vue';
import ContingencyTable from '@/components/DatasetSummary/DataDashboard/ContingencyTable.vue';
import GroupStatsTable from '@/components/DatasetSummary/DataDashboard/GroupStatsTable.vue';

export default {
  name: 'CellDetailDialog',
  components: {
    ContingencyTable,
    GroupStatsTable,
    PlotlyChart,
  },
  props: {
    // dialog open state (v-model)
    value: {
      type: Boolean,
      default: false,
    },
    // the matrix cell being inspected; null while the dialog has never opened
    cell: {
      type: Object,
      default: null,
    },
    // {pval, testName, statRows, pairwise, pairwiseLabel, error} once computed
    stats: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    // the endpoint chooses the test from the data, so which one ran is part of
    // the result rather than something the caller can state up front
    testName() {
      if (this.loading) return 'Computing';
      if (!this.stats || !this.stats.testName) return 'No test available';
      return this.stats.testName;
    },
    comparisonText() {
      if (!this.cell) return '';
      const k = this.cell.levels.length;
      return (
        `Compares ${this.cell.outcomeName} across the ${k} ` +
        `group${k === 1 ? '' : 's'} of ${this.cell.predictorName}.`
      );
    },
    pValueLabel() {
      return this.stats ? formatPValue(this.stats.pval) : '—';
    },
    significant() {
      return !!this.stats && this.stats.pval != null && this.stats.pval < 0.05;
    },
    significanceLabel() {
      if (!this.stats || this.stats.pval == null) return 'no result';
      return this.significant
        ? 'significant at the 0.05 level'
        : 'not significant at the 0.05 level';
    },
    // outliers are worth the extra trace here, unlike in the matrix cells
    traces() {
      if (!this.cell) return [];
      return this.cell.continuous
        ? boxTraces(this.cell.levels, this.cell.stats, {
            showOutliers: true,
            meanSize: MEAN_MARKER_SIZE_LARGE,
          })
        : stackedBarTraces(this.cell.tab, { showLegend: true });
    },
    layout() {
      if (!this.cell) return {};
      const continuous = this.cell.continuous;
      return {
        height: 420,
        autosize: true,
        showlegend: !continuous,
        legend: { orientation: 'h', y: -0.25 },
        barmode: 'stack',
        margin: { t: 10, l: 60, r: 20, b: continuous ? 60 : 90 },
        yaxis: {
          title: {
            text: continuous ? this.cell.outcomeName : 'Number of subjects',
          },
          zeroline: false,
          automargin: true,
          fixedrange: true,
        },
        xaxis: {
          title: { text: this.cell.predictorName },
          type: 'category',
          automargin: true,
          fixedrange: true,
        },
        hoverlabel: {
          bgcolor: '#ffffff',
          font: { family: 'Roboto, sans-serif', size: 14 },
        },
        font: { family: 'Roboto, sans-serif', size: 12 },
        modebar: { remove: ['lasso', 'select', 'pan'] },
      };
    },
  },
  methods: {
    groupSize(level) {
      const found = this.cell.levelCounts.find(l => l.level === level);
      return found ? found.n : 0;
    },
  },
};
</script>

<style scoped>
.dd-caption {
  font-size: 12px;
  color: #777;
}
.dd-test-name {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}
.dd-p-value {
  font-family: monospace;
  font-size: 22px;
  line-height: 1.2;
  color: #333;
}
.dd-sig {
  font-weight: 700;
  color: #c62828;
}
</style>
